import React, {useState, useEffect, useMemo} from 'react';
import Modal from '../../../../Presentation/components/ui/Modal';
import {AppText, AppBodyText, AppLabelText} from '../../../../Presentation/components/ui/Typography';
import AppInput from '../../../../Presentation/components/ui/AppInput';
import AppSelect from '../../../../Presentation/components/ui/AppSelect';
import Button from '../../../../Presentation/components/ui/Button';
import {UpdateUserRequest, UserStatus, DashboardUser} from '../../models/user.model';
import {formatDateTime} from '../../../../Presentation/utils/dateUtils';
import StandardizedErrorHandler from '../../../../Services/common/StandardizedErrorHandler';
import {useRoles} from '../../hooks/useRoles';
import {Loader2} from 'lucide-react';

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (userData: UpdateUserRequest) => Promise<void>;
  loading?: boolean;
  user: DashboardUser | null;
}

const STATUS_OPTIONS = [
  {value: UserStatus.ACTIVE, label: 'نشط'},
  {value: UserStatus.INACTIVE, label: 'غير نشط'},
  {value: UserStatus.SUSPENDED, label: 'معلق'},
  {value: UserStatus.PENDING, label: 'في الانتظار'}
];

export const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  user
}) => {
  const [formData, setFormData] = useState<UpdateUserRequest>({
    id: 0,
    username: '',
    email: '',
    fullName: '',
    roleId: 1,
    status: UserStatus.ACTIVE
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch roles from API
  const {
    roles,
    loading: rolesLoading,
    fetchRoles
  } = useRoles();

  // Fetch roles when modal opens
  useEffect(() => {
    if (open) {
      fetchRoles().catch(error => {
        StandardizedErrorHandler.getInstance().handleError(error, 'EditUserModal.fetchRoles');
      });
    }
  }, [open, fetchRoles]);

  // Transform roles to options format for dropdown
  const roleOptions = useMemo(() => {
    return roles.map(role => ({
      value: Number(role.id),
      label: String(role.displayName || role.name),
      description: role.description ? String(role.description) : undefined
    }));
  }, [roles]);

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        id: user.id,
        username: user.username || '',
        email: user.email || '',
        fullName: user.fullName || '',
        roleId: user.role?.id || 0,
        status: user.status
      });
    }
  }, [user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = 'اسم المستخدم مطلوب';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'الاسم الكامل مطلوب';
    }

    if (!formData.roleId) {
      newErrors.roleId = 'الدور مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      // Handle error using StandardizedErrorHandler to show server message
      StandardizedErrorHandler.getInstance().handleError(error, 'EditUserModal.handleSubmit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof UpdateUserRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };


  if (!user) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="تعديل المستخدم"
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            اسم المستخدم
          </label>
          <AppInput
            type="text"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            placeholder="أدخل اسم المستخدم"
            error={errors.username}
            disabled={loading}
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            البريد الإلكتروني
          </label>
          <AppInput
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="أدخل البريد الإلكتروني"
            error={errors.email}
            disabled={loading}
          />
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            الاسم الكامل
          </label>
          <AppInput
            type="text"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            placeholder="أدخل الاسم الكامل"
            error={errors.fullName}
            disabled={loading}
          />
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            الدور
          </label>
          {rolesLoading ? (
            <div className="flex items-center space-x-2 p-3 border border-gray-300 rounded-md bg-gray-50">
              <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
              <AppBodyText className="text-gray-600">جاري تحميل الأدوار...</AppBodyText>
            </div>
          ) : (
            <AppSelect
              value={formData.roleId}
              onChange={(value) => handleInputChange('roleId', value)}
              options={roleOptions}
              placeholder="اختر الدور"
              error={errors.roleId}
              disabled={loading || rolesLoading}
            />
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            الحالة
          </label>
          <AppSelect
            value={formData.status}
            onChange={(value) => handleInputChange('status', value)}
            options={STATUS_OPTIONS}
            placeholder="اختر الحالة"
            disabled={loading}
          />
        </div>

        {/* Current User Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <AppLabelText className="text-gray-600 mb-2">معلومات المستخدم الحالي:</AppLabelText>
          <div className="space-y-1 text-sm">
            <div><strong>معرف المستخدم:</strong> {user.id}</div>
            <div><strong>تاريخ الإنشاء:</strong> {formatDateTime(user.createdAt)}</div>
            <div><strong>آخر تحديث:</strong> {user.updatedAt ? formatDateTime(user.updatedAt) : 'غير محدد'}</div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={loading || isSubmitting}
            disabled={loading || isSubmitting}
          >
            {loading || isSubmitting ? 'جاري التحديث...' : 'تحديث المستخدم'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

